import { useEffect, Suspense } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { HiOutlineTranslate } from "react-icons/hi";
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import ThemeToggle from '../../components/app/ThemeToggle';
import BrandLogo from '../../components/common/BrandLogo';
import RouteLoadingFallback from '../../components/common/RouteLoadingFallback';
import QuranAmbientBackdrop from '../../components/app/quran/QuranAmbientBackdrop';
import TeenBackground from '../../components/teen/TeenBackground';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { resolveGuestPortal } from '../../lib/routing/viedmaPaths.mjs';

// Module-level guard to prevent URL echo / race-conditions during intentional language toggles
let isTogglingLanguage = false;
let toggleTimeout = null;

export default function GuestLayout({ children }) {
  const { i18n } = useTranslation();
  const { isDark } = useTheme();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'ar';

  // Read language parameter from URL query string (?lang=en, ?lang=ar, ?lng=en, ?lng=ar)
  const queryParam = searchParams.get('lang') || searchParams.get('lng');
  const validQueryLang = queryParam?.toLowerCase() === 'en' 
    ? 'en' 
    : (queryParam?.toLowerCase() === 'ar' ? 'ar' : null);

  // 1. Sync i18n with URL when URL changes from outside (e.g. user typed in address bar or back/forward)
  useEffect(() => {
    // If a button click toggle is in progress, ignore stale URL query param
    if (isTogglingLanguage) return;

    if (validQueryLang && !i18n.language?.startsWith(validQueryLang)) {
      i18n.changeLanguage(validQueryLang);
      localStorage.setItem('i18nextLng', validQueryLang);
    }
  }, [validQueryLang, i18n]);

  // 2. Ensure URL search param is populated on initial load so browser address bar tab reflects language
  useEffect(() => {
    const rawParam = new URLSearchParams(window.location.search).get('lang') || new URLSearchParams(window.location.search).get('lng');
    if (!rawParam) {
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.set('lang', currentLang);
      setSearchParams(nextParams, { replace: true });
    }
  }, []);

  // 3. Language toggle button handler
  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'ar' : 'en';

    // Set guard flag to prevent any echo cancellation
    isTogglingLanguage = true;
    if (toggleTimeout) clearTimeout(toggleTimeout);
    toggleTimeout = setTimeout(() => {
      isTogglingLanguage = false;
    }, 400);

    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);

    const nextParams = new URLSearchParams(window.location.search);
    nextParams.set('lang', newLang);
    nextParams.delete('lng');
    setSearchParams(nextParams, { replace: true });
  };
  const storedPortal = (
    typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('login_target_portal') || sessionStorage.getItem('last_dashboard_portal')) : null
  ) || (
    typeof localStorage !== 'undefined' ? localStorage.getItem('last_dashboard_portal') : null
  );

  const currentPortal = resolveGuestPortal(searchParams, storedPortal);

  // Sync URL search param if portal was restored from storage
  useEffect(() => {
    const portalInUrl = searchParams.get('portal');
    if (!portalInUrl && currentPortal !== 'app') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('portal', currentPortal);
      if (currentPortal === 'viedma' && !nextParams.has('redirect')) {
        nextParams.set('redirect', '/app/viedma');
      } else if (currentPortal === 'quran' && !nextParams.has('redirect')) {
        nextParams.set('redirect', '/quran');
      }
      setSearchParams(nextParams, { replace: true });
    }
  }, [currentPortal, searchParams, setSearchParams]);

  const handlePortalToggle = (targetPortal) => {
    const nextParams = new URLSearchParams(searchParams);

    if (targetPortal === 'viedma') {
      nextParams.set('portal', 'viedma');
      nextParams.set('redirect', '/app/viedma');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('last_dashboard_portal', 'viedma');
        sessionStorage.setItem('login_target_portal', 'viedma');
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('last_dashboard_portal', 'viedma');
      }
    } else if (targetPortal === 'quran') {
      nextParams.set('portal', 'quran');
      nextParams.set('redirect', '/quran');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('last_dashboard_portal', 'quran');
        sessionStorage.setItem('login_target_portal', 'quran');
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('last_dashboard_portal', 'quran');
      }
    } else {
      // Default: 'app'
      nextParams.set('portal', 'app');
      nextParams.set('redirect', '/app');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('last_dashboard_portal', 'app');
        sessionStorage.setItem('login_target_portal', 'app');
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('last_dashboard_portal', 'app');
      }
    }
    setSearchParams(nextParams, { replace: true });
  };

  const isRegister = location.pathname.includes('register');
  const cardWidthClass = isRegister ? 'max-w-[540px]' : 'max-w-[420px]';

  const containerBgClass = currentPortal === 'quran'
    ? 'bg-white dark:bg-[#171614] text-[#1c1a17] dark:text-[#f3ebdd]'
    : currentPortal === 'viedma'
      ? 'bg-[#fffdf5] dark:bg-[#0c1723] text-[#19374c] dark:text-[#fff6e5]'
      : 'bg-[#f4f7fa] dark:bg-page-base text-[#1a2344] dark:text-text-main';

  const cardSurfaceClass = currentPortal === 'quran'
    ? 'bg-[#fffdfa] dark:bg-[#0e221b] border-[#c59b27]/40 dark:border-[#c59b27]/40 shadow-[0_20px_50px_rgba(197,155,39,0.08)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.6)]'
    : currentPortal === 'viedma'
      ? 'bg-white dark:bg-[#152635] border-slate-200/90 dark:border-white/10 shadow-[0_20px_50px_rgba(25,55,76,0.12)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.6)]'
      : 'bg-white dark:bg-page-surface/80 dark:backdrop-blur-xl border-transparent shadow-[0_15px_35px_rgba(0,0,0,0.05)] dark:shadow-card-shadow';

  return (
    <div
      className={`min-h-screen flex flex-col ${containerBgClass} transition-colors duration-200 ease-out font-sans relative overflow-x-hidden`}
    >
      {/* 0. Dedicated Portal Ambient Background */}
      {currentPortal === 'quran' && (
        <div className="fixed inset-0 pointer-events-none -z-10" style={{ zIndex: 0 }}>
          <QuranAmbientBackdrop />
        </div>
      )}
      {currentPortal === 'viedma' && (
        <div className="fixed inset-0 pointer-events-none -z-10" style={{ zIndex: 0 }}>
          <TeenBackground isViedma={true} />
        </div>
      )}

      {/* 1. HEADER SECTION */}
      <header className="relative z-10 w-full pt-6 sm:pt-8 pb-4 px-4 md:px-16 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center h-16 sm:h-20 w-56 sm:w-72">
          <BrandLogo
            to={`/login?portal=${currentPortal}`}
            brand={currentPortal === 'viedma' ? 'viedma' : currentPortal === 'quran' ? 'quran' : undefined}
            variant="guest"
          />
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Quick Portal Switcher */}
          <div className="flex items-center gap-1 p-1 bg-slate-200/70 dark:bg-white/10 backdrop-blur-md border border-slate-300/80 dark:border-white/10 rounded-full shadow-inner">
            {/* 1. Viedma (Second Picture Logo) */}
            <button
              type="button"
              onClick={() => handlePortalToggle('viedma')}
              className={`h-8 px-2.5 sm:px-3 rounded-full transition-all duration-150 flex items-center justify-center cursor-pointer select-none ${
                currentPortal === 'viedma'
                  ? 'bg-white dark:bg-[#1e222b] shadow-sm ring-2 ring-[#e94330] opacity-100 font-bold'
                  : 'bg-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5 opacity-50 hover:opacity-85'
              }`}
              title={currentLang === 'en' ? 'Viedma STEAM (/app/viedma)' : 'منصة فيدما (/app/viedma)'}
            >
              <span className="font-black text-xs sm:text-sm font-sans flex items-center select-none" dir="ltr" aria-label="VIEDMA">
                <span style={{ color: '#2b7fb3' }}>V</span>
                <span className={currentPortal === 'viedma' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}>I</span>
                <span style={{ color: '#e94330' }}>E</span>
                <span style={{ color: '#d97706' }}>D</span>
                <span style={{ color: '#2b7fb3' }}>M</span>
                <span style={{ color: '#16a34a' }}>A</span>
              </span>
            </button>

            {/* 2. Abjd / App (Fourth Picture Logo - IN THE MIDDLE) */}
            <button
              type="button"
              onClick={() => handlePortalToggle('app')}
              className={`h-8 px-2.5 sm:px-3 rounded-full transition-all duration-150 flex items-center justify-center cursor-pointer select-none ${
                currentPortal === 'app'
                  ? 'bg-white dark:bg-[#1e222b] shadow-sm ring-2 ring-blue-500 opacity-100'
                  : 'bg-transparent hover:bg-white/50 dark:hover:bg-white/5 opacity-50 hover:opacity-85'
              }`}
              title={currentLang === 'en' ? 'Main App (/app)' : 'المنصة الرئيسية (/app)'}
            >
              <img
                src={isDark ? "/branding/logo-minimal-dark-cropped.png?v=9" : "/branding/logo-minimal-cropped.png?v=9"}
                alt="أبجد"
                className="h-5 sm:h-5.5 w-auto object-contain filter drop-shadow-xs -translate-y-[3px]"
              />
            </button>

            {/* 3. Quran (First Picture Icon) */}
            <button
              type="button"
              onClick={() => handlePortalToggle('quran')}
              className={`h-8 px-2.5 sm:px-3 rounded-full text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 cursor-pointer select-none ${
                currentPortal === 'quran'
                  ? 'bg-[#0d382c] text-[#f3e5b8] shadow-sm ring-2 ring-[#c59b27] opacity-100'
                  : 'bg-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5 opacity-50 hover:opacity-85'
              }`}
              title={currentLang === 'en' ? 'Quran Portal (/quran)' : 'منصة القرآن الكريم (/quran)'}
            >
              <svg viewBox="0 0 100 100" className="w-5 h-5 shrink-0 select-none" aria-hidden="true">
                <rect x="14" y="14" width="72" height="72" rx="9" fill="#0d382c" stroke="#c59b27" strokeWidth="4.5" />
                <rect x="14" y="14" width="72" height="72" rx="9" transform="rotate(45 50 50)" fill="#0d382c" stroke="#c59b27" strokeWidth="4.5" />
                <circle cx="50" cy="50" r="28" fill="#144d3d" stroke="#c59b27" strokeWidth="2.5" strokeDasharray="3.5 3" />
                <circle cx="50" cy="50" r="22.5" fill="none" stroke="#c59b27" strokeWidth="1.5" />
                <path d="M50 43 C46 39 37 38 31 40 L31 63 C37 61 46 62 50 66 C54 62 63 61 69 63 L69 40 C63 38 54 39 50 43 Z" fill="#ffffff" stroke="#c59b27" strokeWidth="1.2" />
                <path d="M50 43 L50 66" stroke="#c59b27" strokeWidth="1.6" />
              </svg>
              <span className="leading-none">{currentLang === 'en' ? 'Quran' : 'القرآن'}</span>
            </button>
          </div>

          <ThemeToggle />
          <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-white/10 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-full text-slate-700 dark:text-white text-sm font-semibold hover:bg-white dark:hover:bg-white/20 hover:shadow-md active:scale-95 transition-all duration-200 cursor-pointer"
        >
          <HiOutlineTranslate className="text-lg text-blue-600 dark:text-white" />
          <span>{currentLang === 'en' ? 'العربية' : 'English'}</span>
        </button>
        </div>
      </header>

      {/* 2. MIDDLE SECTION */}
      <main className="relative z-10 flex-1 flex justify-center items-center py-6 px-5">
        <div 
          className={`relative z-10 w-full ${cardWidthClass} p-6 sm:p-10 ${cardSurfaceClass} rounded-[40px] text-center border transition-all duration-300 ${currentPortal !== 'app' ? 'min-h-[640px] flex flex-col justify-center overflow-hidden' : ''}`}
          style={{
            backgroundColor: currentPortal === 'quran'
              ? (isDark ? '#0e211b' : '#fffdfa')
              : currentPortal === 'viedma'
                ? (isDark ? '#152635' : '#ffffff')
                : undefined,
          }}
        >
          <div className="w-full">
            <Suspense fallback={<RouteLoadingFallback />}>
              {children || <Outlet />}
            </Suspense>
          </div>
        </div>
      </main>

      {/* 3. FOOTER SECTION */}
      <footer 
        className="relative z-30 w-full py-8 px-4 md:px-16 flex flex-col md:flex-row justify-between items-center gap-4 border-t border-slate-200/60 dark:border-white/10 max-w-7xl mx-auto w-full mt-auto"
        style={{ position: 'relative', zIndex: 30 }}
      >
        <p 
          dir="ltr"
          className={`font-medium text-xs text-center md:text-start transition-colors duration-200 ${
            currentPortal === 'quran'
              ? 'text-[#536b60] dark:text-[#a6bcb0]'
              : currentPortal === 'viedma'
                ? 'text-slate-600 dark:text-slate-400'
                : 'text-[#94a3b8] dark:text-text-muted'
          }`}
        >
          © 2026 abjd. All rights reserved.
        </p>
        <nav className="flex flex-wrap justify-center gap-8 text-xs">
          <a 
            href="#" 
            className={`transition-colors ${
              currentPortal === 'quran'
                ? 'text-[#536b60] dark:text-[#a6bcb0] hover:text-[#0d382c] dark:hover:text-[#c59b27]'
                : currentPortal === 'viedma'
                  ? 'text-slate-600 dark:text-slate-400 hover:text-[#176c8e] dark:hover:text-[#78c8e8]'
                  : 'text-[#94a3b8] dark:text-text-muted hover:text-[#64748b] dark:hover:text-primary-accent'
            } hover:underline`}
          >
            Academic Integrity
          </a>
          <a 
            href="#" 
            className={`transition-colors ${
              currentPortal === 'quran'
                ? 'text-[#536b60] dark:text-[#a6bcb0] hover:text-[#0d382c] dark:hover:text-[#c59b27]'
                : currentPortal === 'viedma'
                  ? 'text-slate-600 dark:text-slate-400 hover:text-[#176c8e] dark:hover:text-[#78c8e8]'
                  : 'text-[#94a3b8] dark:text-text-muted hover:text-[#64748b] dark:hover:text-primary-accent'
            } hover:underline`}
          >
            Privacy Standards
          </a>
          <a 
            href="#" 
            className={`transition-colors ${
              currentPortal === 'quran'
                ? 'text-[#536b60] dark:text-[#a6bcb0] hover:text-[#0d382c] dark:hover:text-[#c59b27]'
                : currentPortal === 'viedma'
                  ? 'text-slate-600 dark:text-slate-400 hover:text-[#176c8e] dark:hover:text-[#78c8e8]'
                  : 'text-[#94a3b8] dark:text-text-muted hover:text-[#64748b] dark:hover:text-primary-accent'
            } hover:underline`}
          >
            Terms of Service
          </a>
        </nav>
      </footer>
    </div>
  );
}
